import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import { VOLUMES, getIssue, issueFullPdf } from './data/wp-structured'

const P_ISSN = '2583-004X'
const E_ISSN = '2582-7154'

function yearOf(v: { range?: string; month?: string; label?: string }): string {
    const m = `${v.range ?? ''} ${v.month ?? ''} ${v.label ?? ''}`.match(/(20\d{2})/)
    return m ? m[1] : '—'
}

export default function Archives() {
    const [year, setYear] = useState<string>('all')
    const [filtersOpen, setFiltersOpen] = useState(false)

    const years = useMemo(() => {
        const set = new Set<string>()
        VOLUMES.forEach((v) => { const y = yearOf(v); if (y !== '—') set.add(y) })
        return Array.from(set).sort((a, b) => Number(b) - Number(a))
    }, [])

    const results = useMemo(
        () => (year === 'all' ? VOLUMES : VOLUMES.filter((v) => yearOf(v) === year)),
        [year]
    )

    return (
        <>
            <SEO
                title="Archives"
                description="Every issue of Indian Studies Review since Volume 1 (2020). Browse all volumes and read articles open-access."
                path="/archives"
            />
            <PageHeader
                eyebrow="Archives"
                title="Browse all issues"
                lede="The complete record of Indian Studies Review since Volume 1 (2020). Filter by year, then open any issue to read its articles — all open access."
            />

            <section className="section-tight">
                <div className="container-prose grid lg:grid-cols-[17rem_1fr] gap-10 items-start">
                    <aside className="lg:sticky lg:top-28 space-y-6">
                        <button
                            type="button"
                            onClick={() => setFiltersOpen((v) => !v)}
                            className="lg:hidden w-full flex items-center justify-between px-4 py-3 text-[13px] font-semibold"
                            style={{ border: '1px solid var(--color-rule-strong)', background: '#fff', color: 'var(--color-ink-700)' }}
                        >
                            <span>Filter by year{year !== 'all' ? ` · ${year}` : ''}</span>
                            <svg
                                width="14" height="14" viewBox="0 0 10 6" fill="none" stroke="currentColor"
                                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                style={{ transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}
                            >
                                <path d="M1 1l4 4 4-4" />
                            </svg>
                        </button>
                        <div className={`${filtersOpen ? 'block' : 'hidden'} lg:block space-y-6`}>
                        <div style={{ border: '1px solid var(--color-rule)', background: '#fff' }}>
                            <div className="px-5 py-3 font-display" style={{ fontSize: '1.05rem', color: 'var(--color-ink-700)', borderBottom: '1px solid var(--color-rule)' }}>
                                Publication year
                            </div>
                            <div className="p-4">
                                <select
                                    value={year}
                                    onChange={(e) => setYear(e.target.value)}
                                    className="w-full px-3 py-2.5 text-[14px] bg-white"
                                    style={{ border: '1px solid var(--color-rule-strong)', color: 'var(--color-ink-700)', borderRadius: '2px' }}
                                >
                                    <option value="all">All years</option>
                                    {years.map((y) => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {(['all', ...years] as const).map((y) => {
                                        const active = year === y
                                        return (
                                            <button
                                                key={y}
                                                onClick={() => setYear(y)}
                                                className="px-2.5 py-1 text-[11px] uppercase font-semibold transition-colors"
                                                style={{
                                                    letterSpacing: '0.08em',
                                                    color: active ? 'var(--color-paper-100)' : 'var(--color-ink-500)',
                                                    background: active ? 'var(--color-gold-700)' : 'transparent',
                                                    border: `1px solid ${active ? 'var(--color-gold-700)' : 'var(--color-rule-strong)'}`
                                                }}
                                            >
                                                {y === 'all' ? 'All' : y}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>

                        <div style={{ border: '1px solid var(--color-rule)', background: '#fff' }}>
                            <div className="px-5 py-3 font-display" style={{ fontSize: '1.05rem', color: 'var(--color-ink-700)', borderBottom: '1px solid var(--color-rule)' }}>
                                Access
                            </div>
                            <div className="p-4">
                                <div className="inline-flex items-center gap-2 px-3 py-2 text-[12px] font-semibold" style={{ background: '#D9E5D6', color: '#1E4A2A', border: '1px solid #8DB78A' }}>
                                    <span style={{ width: 7, height: 7, borderRadius: 999, background: '#2E6B3B' }} /> Open Access — every issue
                                </div>
                                <p className="mt-3 text-[12.5px] leading-relaxed" style={{ color: 'var(--color-ink-400)' }}>
                                    All content is freely available without a subscription, login, or paywall.
                                </p>
                            </div>
                        </div>

                        <div style={{ border: '1px solid var(--color-rule)', background: 'var(--color-paper-50)' }}>
                            <div className="px-5 py-4 text-[12px] leading-relaxed" style={{ color: 'var(--color-ink-500)' }}>
                                <div><span className="font-semibold" style={{ color: 'var(--color-ink-700)' }}>P-ISSN</span> {P_ISSN}</div>
                                <div className="mt-1"><span className="font-semibold" style={{ color: 'var(--color-ink-700)' }}>E-ISSN</span> {E_ISSN}</div>
                                <div className="mt-1"><span className="font-semibold" style={{ color: 'var(--color-ink-700)' }}>Frequency</span> Bi-annual</div>
                            </div>
                        </div>
                        </div>
                    </aside>

                    <div>
                        <div className="pb-4 mb-2 text-[14px]" style={{ borderBottom: '1px solid var(--color-rule)', color: 'var(--color-ink-500)' }}>
                            <span className="font-semibold" style={{ color: 'var(--color-ink-700)' }}>{results.length}</span>{' '}
                            {results.length === 1 ? 'issue' : 'issues'}
                            {year !== 'all' && <span> · {year}</span>}
                        </div>

                        <ul>
                            {results.map((v, i) => {
                                const articles = getIssue(v.slug)
                                const fullPdf = issueFullPdf(v)
                                return (
                                    <motion.li
                                        key={v.slug}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: (i % 6) * 0.03 }}
                                        className="py-6 grid grid-cols-[5.5rem_1fr] sm:grid-cols-[7rem_1fr] gap-5"
                                        style={{ borderBottom: '1px solid var(--color-rule)' }}
                                    >
                                        <Link to={`/archives/${v.slug}`} className="block shrink-0">
                                            <div className="aspect-[3/4] overflow-hidden" style={{ background: 'var(--color-paper-100)', border: '1px solid var(--color-rule-strong)' }}>
                                                <img src={v.cover} alt={v.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.03]" />
                                            </div>
                                        </Link>

                                        <div className="min-w-0">
                                            <Link
                                                to={`/archives/${v.slug}`}
                                                className="font-display transition-colors hover:text-[var(--color-gold-700)]"
                                                style={{ fontSize: 'clamp(1.15rem, 1.8vw, 1.4rem)', color: 'var(--color-ink-700)', letterSpacing: '-0.014em', lineHeight: 1.2, fontWeight: 500 }}
                                            >
                                                {v.label}
                                            </Link>

                                            <dl className="mt-3 space-y-1 text-[13.5px]" style={{ color: 'var(--color-ink-500)' }}>
                                                {v.range && (
                                                    <div><span className="font-semibold" style={{ color: 'var(--color-ink-700)' }}>Published:</span> {v.range}</div>
                                                )}
                                                <div>
                                                    <span className="font-semibold" style={{ color: 'var(--color-ink-700)' }}>Articles:</span>{' '}
                                                    {articles.length > 0 ? `${articles.length} article${articles.length === 1 ? '' : 's'}` : '—'}
                                                </div>
                                                <div>
                                                    <span className="font-semibold" style={{ color: 'var(--color-ink-700)' }}>ISSN:</span> {P_ISSN}
                                                    <span className="mx-2" style={{ color: 'var(--color-rule-strong)' }}>|</span>
                                                    <span className="font-semibold" style={{ color: 'var(--color-ink-700)' }}>Online ISSN:</span> {E_ISSN}
                                                </div>
                                            </dl>

                                            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                                                <Link
                                                    to={`/archives/${v.slug}`}
                                                    className="inline-block text-[11px] uppercase font-semibold"
                                                    style={{ letterSpacing: '0.18em', color: 'var(--color-gold-700)', borderBottom: '1px solid var(--color-gold-300)', paddingBottom: '2px' }}
                                                >
                                                    View issue →
                                                </Link>
                                                {fullPdf && (
                                                    <a
                                                        href={fullPdf}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-block text-[11px] uppercase font-semibold"
                                                        style={{ letterSpacing: '0.18em', color: 'var(--color-gold-700)', borderBottom: '1px solid var(--color-gold-300)', paddingBottom: '2px' }}
                                                    >
                                                        View full PDF ↓
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </motion.li>
                                )
                            })}
                        </ul>

                        {results.length === 0 && (
                            <div className="text-center py-16" style={{ color: 'var(--color-ink-400)' }}>
                                No issues published in {year}.
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    )
}
